import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Hand } from 'lucide-react';
import { InstantFeedback } from './board/InstantFeedback';

const norm = (w) => String(w || '').trim().toLowerCase();
const accepts = (gap, word) => [gap.correct_word, ...(gap.accepted_words || [])].map(norm).includes(norm(word));

const FillGapsElement = ({ config, element, progress, onComplete, onSaveData, isCompleted }) => {
  const template = config.template_text || '';
  const gaps = config.gaps || [];
  const wordOptions = config.word_options?.length ? config.word_options : gaps.map(g => g.correct_word);
  const [filled, setFilled] = useState(() => {
    if (isCompleted) return Object.fromEntries(gaps.map(g => [g.id, g.correct_word]));
    const saved = progress?.element_progress?.[element?.id]?.answers || {};
    return Object.fromEntries(Object.entries(saved).filter(([id, w]) => { const g = gaps.find(x => x.id === id); return g && accepts(g, w); }));
  });
  const [words] = useState(() => [...wordOptions].sort(() => Math.random() - 0.5));
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [flashGap, setFlashGap] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const used = Object.values(filled);
  const bank = words.map((w, i) => ({ w, i })).filter(({ w }) => {
    const usedCount = used.filter(u => u === w).length;
    const index = words.filter(x => x === w).length;
    return usedCount < index;
  });
  const done = gaps.length > 0 && gaps.every(g => filled[g.id]);

  const place = (gapId, word) => {
    const gap = gaps.find(g => g.id === gapId);
    if (!gap || filled[gapId] || !word) return;
    const ok = accepts(gap, word);
    setSelected(null);
    setFeedback({ ok, key: Date.now() });
    setFlashGap({ id: gapId, ok });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setFlashGap(null), 1200);
    if (!ok) return;
    const next = { ...filled, [gapId]: word };
    setFilled(next);
    const allDone = gaps.every(g => next[g.id]);
    onSaveData({ answers: next, correct: allDone, score: `${Object.keys(next).length}/${gaps.length}` });
    if (allDone && !isCompleted) { onComplete(); toast.success('All gaps filled correctly!'); }
  };

  const parts = template.split(/(\{gap\})/g);
  let gapIndex = 0;

  return (
    <div className="space-y-4" data-testid="fill-gaps-element">
      {config.instructions && <p className="text-sm text-slate-600">{config.instructions}</p>}
      <InstantFeedback feedback={feedback} testId="fill-gaps-feedback" />
      <div className="p-5 bg-white border border-slate-200 rounded-xl leading-[2.6] text-base text-slate-800" data-testid="fill-gaps-text">
        {parts.map((part, i) => {
          if (part !== '{gap}') return <span key={`t-${i}`}>{part}</span>;
          const gap = gaps[gapIndex++];
          if (!gap) return null;
          const word = filled[gap.id];
          const f = flashGap?.id === gap.id ? flashGap : null;
          return (
            <span
              key={`g-${gap.id}`}
              role="button"
              tabIndex={0}
              onClick={() => place(gap.id, selected)}
              onKeyDown={(e) => { if (e.key === 'Enter') place(gap.id, selected); }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); place(gap.id, e.dataTransfer.getData('text/plain')); }}
              className={`inline-flex items-center justify-center min-w-[110px] min-h-[44px] mx-1 px-3 border-2 rounded-lg align-middle transition-colors duration-200 ${
                word ? 'border-green-400 bg-green-50 text-green-800 font-medium' :
                f && !f.ok ? 'border-red-400 bg-red-50 animate-[shake_0.3s_ease-in-out]' :
                selected ? 'border-[#20acf6] border-dashed bg-[#20acf6]/5 cursor-pointer' : 'border-slate-300 border-dashed bg-slate-50 text-slate-400'
              }`}
              data-testid={`gap-${gap.id}`}
            >
              {word ? <>{word}<CheckCircle2 className="h-3.5 w-3.5 ml-1.5 text-green-600" /></> : <span className="text-sm">{selected ? 'Tap to place' : '___'}</span>}
            </span>
          );
        })}
      </div>

      {done ? (
        <div className="p-4 rounded-xl bg-green-50 border border-green-200 text-sm font-medium text-green-800 flex items-center gap-2" data-testid="fill-gaps-score">
          <CheckCircle2 className="h-5 w-5 text-green-600" /> All {gaps.length} gaps correct — well done!
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-slate-500 flex items-center gap-1.5"><Hand className="h-3.5 w-3.5" /> Tap a word, then tap the gap (or drag it on desktop).</p>
          <div className="flex flex-wrap gap-2 p-3 bg-[#F8FAFC] rounded-xl min-h-[60px]" data-testid="word-bank">
            {bank.map(({ w, i }) => (
              <button
                key={`${w}-${i}`}
                type="button"
                draggable
                onDragStart={(e) => { e.dataTransfer.setData('text/plain', w); setSelected(w); }}
                onClick={() => setSelected(selected === w ? null : w)}
                className={`min-h-[44px] px-4 py-2 rounded-lg text-sm font-medium transition-[transform,background-color] duration-150 select-none ${
                  selected === w ? 'bg-[#02d1d4] text-white shadow-md scale-105' : 'bg-white border border-[#20acf6]/30 text-[#2a3e5d] hover:bg-[#20acf6]/10 cursor-grab'
                }`}
                data-testid={`word-tile-${i}`}
                aria-pressed={selected === w}
              >
                {w}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FillGapsElement;
